// src/components/TrackOrder/TrackOrder.jsx
import React, { useState, useEffect } from "react";
import { Package, CheckCircle, Truck, Clock, Phone, Loader } from "lucide-react";

export default function TrackOrder() {
  // State for order lookup form
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Extract email from localStorage on component mount
  useEffect(() => {
    const extractEmailFromLocalStorage = () => {
      try {
        // Look for Auth0 user data in localStorage
        const authKey = Object.keys(localStorage).find(key => 
          key.includes('@@auth0spajs@@') && key.includes('@@user@@')
        );
        
        if (authKey) {
          const userData = JSON.parse(localStorage.getItem(authKey));
          if (userData && userData.decodedToken && userData.decodedToken.claims) {
            const userEmail = userData.decodedToken.claims.email;
            if (userEmail) {
              setEmail(userEmail);
              return;
            }
          }
        }

        // Fallback: look for access token to extract email
        const tokenKey = Object.keys(localStorage).find(key => 
          key.includes('@@auth0spajs@@') && key.includes('https://masho-api')
        );
        
        if (tokenKey) {
          const tokenData = JSON.parse(localStorage.getItem(tokenKey));
          // You might need to decode the access token or make an API call to get user info
          //console.log('Token data found, but email extraction from access token requires additional logic');
        }

      } catch (error) {
        console.error('Error extracting email from localStorage:', error);
      }
    };

    extractEmailFromLocalStorage();
  }, []);

  // Handle form submission to look up the order
  const handleLookup = async (e) => {
    e.preventDefault();
    setError("");
    setOrder(null);
    setLoading(true);

    if (!email) {
      setError("Email not found. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      // Clean order ID (remove # if present and trim whitespace)
      const cleanOrderId = orderId.replace('#', '').trim();
      
      // Make API call to track order
      const response = await fetch(
        `${API_BASE_URL}/track-order/?order_id=${encodeURIComponent(cleanOrderId)}&email=${encodeURIComponent(email)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            // Add authorization header if needed
            // 'Authorization': `Bearer ${getAccessToken()}`,
          },
        }
      );

      const data = await response.json();

      if (data.success && data.order) {
        setOrder(data.order);
        //console.log('Order data:', data.order);
      } else {
        setError(data.error || "Order not found. Please check your order ID.");
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      setError("Failed to fetch order details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get access token (if needed for API authentication)
  const getAccessToken = () => {
    try {
      const tokenKey = Object.keys(localStorage).find(key => 
        key.includes('@@auth0spajs@@') && key.includes('https://masho-api')
      );
      
      if (tokenKey) {
        const tokenData = JSON.parse(localStorage.getItem(tokenKey));
        return tokenData?.body?.access_token;
      }
    } catch (error) {
      console.error('Error getting access token:', error);
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4 md:px-8 mt-16">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="text-3xl md:text-4xl text-[#333333] text-center mb-8 relative">
          Track Your Order
          <span className="block w-24 h-1 bg-[#D4AF37] mx-auto mt-2"></span>
        </h1>

        {/* Current User Info */}
        {email && (
          <div className="mb-6 text-center">
            {/* <p className="text-sm text-gray-600">
              Tracking orders for: <span className="font-medium text-[#333333]">{email}</span>
            </p> */}
          </div>
        )}

        {/* Order Lookup Form */}
        <section className="mb-12">
          <form onSubmit={handleLookup} className="flex flex-col md:flex-row gap-4 justify-center">
            <input
              type="text"
              placeholder="Order ID (e.g., #7e7f50d9)"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              className="w-full md:w-1/2 py-2 px-4 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm"
              required
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !email}
              className="bg-[#D4AF37] text-black px-6 py-2 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Tracking...
                </>
              ) : (
                'Track Order'
              )}
            </button>
          </form>
          {error && (
            <p className="text-red-500 text-center mt-4 text-sm">{error}</p>
          )}
          {!email && (
            <p className="text-yellow-600 text-center mt-4 text-sm">
              Please log in to track your orders.
            </p>
          )}
        </section>

        {/* Order Details and Tracking (shown after lookup) */}
        {order && (
          <>
            {/* Order Details */}
            <section className="mb-12 bg-white p-6 rounded-sm border border-gray-200 shadow-sm">
              <h2 className="text-2xl text-[#333333] mb-4">
                Order Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">
                    <strong>Order ID:</strong> {order.orderId}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Order Date:</strong> {order.date}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Shipping Address:</strong> {order.shippingAddress}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Payment Status:</strong> 
                    <span className={`ml-1 px-2 py-1 rounded text-xs ${
                      order.paymentStatus === 'completed' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {order.paymentStatus}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Items Ordered:</strong>
                  </p>
                  {order.items.map((item, index) => (
                    <div key={index} className="text-sm text-gray-600 mb-2">
                      <div className="flex items-center gap-2">
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-8 h-8 object-cover rounded"
                          />
                        )}
                        <div>
                          <p>- {item.name} ({item.size})</p>
                          <p className="text-xs text-gray-500">
                            Qty: {item.quantity} - ₹{item.price} each = ₹{item.total_price}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="mt-4 pt-2 border-t">
                    <p className="text-sm text-gray-600">
                      <strong>Subtotal:</strong> ₹{order.subtotal}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Shipping:</strong> ₹{order.shippingCost}
                    </p>
                    {order.discount > 0 && (
                      <p className="text-sm text-gray-600">
                        <strong>Discount:</strong> -₹{order.discount}
                      </p>
                    )}
                    <p className="text-sm text-gray-600 font-bold">
                      <strong>Total:</strong> ₹{order.total}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Tracking Status */}
            <section className="mb-12">
              <h2 className="text-2xl text-[#333333] text-center mb-6">
                Tracking Status
              </h2>
              <div className="relative">
                {/* Mobile View - Vertical Layout */}
                <div className="block md:hidden">
                  {order.status.map((step, index) => (
                    <div key={index} className="flex items-center mb-4">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                          step.completed
                            ? "bg-[#D4AF37] text-black"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {step.stage === "Order Placed" && <Package className="w-5 h-5" />}
                        {step.stage === "Processing" && <Clock className="w-5 h-5" />}
                        {step.stage === "Shipped" && <Truck className="w-5 h-5" />}
                        {step.stage === "Out for Delivery" && <Truck className="w-5 h-5" />}
                        {step.stage === "Delivered" && <CheckCircle className="w-5 h-5" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{step.stage}</p>
                        <p className="text-xs text-gray-500">{step.date}</p>
                      </div>
                      {index < order.status.length - 1 && step.completed && (
                        <div className="absolute left-5 mt-10 w-0.5 h-4 bg-[#D4AF37]"></div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop View - Horizontal Layout */}
                <div className="hidden md:block">
                  <div className="flex justify-between items-center">
                    {order.status.map((step, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                            step.completed
                              ? "bg-[#D4AF37] text-black"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {step.stage === "Order Placed" && <Package className="w-5 h-5" />}
                          {step.stage === "Processing" && <Clock className="w-5 h-5" />}
                          {step.stage === "Shipped" && <Truck className="w-5 h-5" />}
                          {step.stage === "Out for Delivery" && <Truck className="w-5 h-5" />}
                          {step.stage === "Delivered" && <CheckCircle className="w-5 h-5" />}
                        </div>
                        <p className="text-xs text-gray-600 text-center">{step.stage}</p>
                        <p className="text-xs text-gray-500">{step.date}</p>
                      </div>
                    ))}
                  </div>
                  {/* Progress Line */}
                  <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200">
                    <div
                      className="h-full bg-[#D4AF37]"
                      style={{
                        width: `${
                          Math.max(0, (order.status.filter((s) => s.completed).length - 1) * 25)
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </section>

            {/* Estimated Delivery */}
            <section className="mb-12 text-center">
              <h2 className="text-2xl text-[#333333] mb-4">
                {order.currentStatus === 'delivered' ? 'Delivery Status' : 'Estimated Delivery'}
              </h2>
              <p className={`text-lg font-medium ${
                order.currentStatus === 'delivered' 
                  ? 'text-green-600' 
                  : 'text-[#D4AF37]'
              }`}>
                {order.estimatedDelivery}
              </p>
            </section>

            {/* Customer Support */}
            <section className="text-center">
              <h2 className="text-2xl text-[#333333] mb-4">
                Need Help?
              </h2>
              <p className="text-gray-600 text-sm mb-4">
                Our team is here to assist you with your order.
              </p>
              <div className="flex justify-center gap-4 mb-6">
                <p className="text-sm text-gray-600 flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-[#D4AF37]" />
                  +91 9567883512
                </p>
              </div>
              <a
                href="tel:+919567883512"
                className="inline-block bg-[#D4AF37] text-black px-6 py-2 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200"
              >
                Contact Support
              </a>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
